/**
 * Image Validation Utilities
 *
 * Centralized image validation for uploads with proper size limits
 * to prevent 413 Content Too Large errors
 */

/**
 * Image size limits
 */
export const IMAGE_LIMITS = {
  // Original file size limits (before processing)
  MAX_FILE_SIZE_MB: 10, // 10MB for original file
  MAX_FILE_SIZE_BYTES: 10 * 1024 * 1024,

  // Base64 size limits (after encoding)
  // AWS API Gateway max: 10MB
  // AWS Lambda max: 6MB
  // Safe limit accounting for other form data: 4MB
  MAX_BASE64_SIZE_MB: 4,
  MAX_BASE64_SIZE_BYTES: 4 * 1024 * 1024,

  // Image dimension recommendations
  RECOMMENDED_MAX_WIDTH: 1920,
  RECOMMENDED_MAX_HEIGHT: 1080,

  // Supported formats
  SUPPORTED_FORMATS: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
};

/**
 * Validate file before upload
 */
export function validateImageFile(file: File): { valid: boolean; error?: string } {
  // Check if file exists
  if (!file) {
    return { valid: false, error: 'No file selected' };
  }

  // Check file type
  if (!IMAGE_LIMITS.SUPPORTED_FORMATS.includes(file.type)) {
    return {
      valid: false,
      error: 'Please upload a valid image file (JPEG, PNG, or WebP)',
    };
  }

  // Check file size
  if (file.size > IMAGE_LIMITS.MAX_FILE_SIZE_BYTES) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
    return {
      valid: false,
      error: `Image size (${sizeMB}MB) exceeds maximum allowed size of ${IMAGE_LIMITS.MAX_FILE_SIZE_MB}MB`,
    };
  }

  return { valid: true };
}

/**
 * Validate base64 string size
 */
export function validateBase64Size(base64String: string): { valid: boolean; error?: string } {
  if (!base64String) {
    return { valid: false, error: 'No image data provided' };
  }

  // Calculate actual byte size of base64 string
  const base64Length = base64String.length - 'data:image/jpeg;base64,'.length;
  const sizeInBytes = (base64Length * 3) / 4;

  if (sizeInBytes > IMAGE_LIMITS.MAX_BASE64_SIZE_BYTES) {
    const sizeMB = (sizeInBytes / (1024 * 1024)).toFixed(2);
    return {
      valid: false,
      error: `Processed image size (${sizeMB}MB) is too large. Please try a smaller image or lower quality setting.`,
    };
  }

  return { valid: true };
}

/**
 * Get human-readable file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Calculate base64 size from string
 */
export function getBase64Size(base64String: string): number {
  const base64Length = base64String.length - 'data:image/jpeg;base64,'.length;
  return (base64Length * 3) / 4;
}

/**
 * Validate image dimensions
 */
export function validateImageDimensions(
  width: number,
  height: number
): { valid: boolean; warning?: string } {
  if (width > IMAGE_LIMITS.RECOMMENDED_MAX_WIDTH || height > IMAGE_LIMITS.RECOMMENDED_MAX_HEIGHT) {
    return {
      valid: true, // Still valid, just a warning
      warning: `Image dimensions (${width}x${height}) exceed recommended size of ${IMAGE_LIMITS.RECOMMENDED_MAX_WIDTH}x${IMAGE_LIMITS.RECOMMENDED_MAX_HEIGHT}. Image will be automatically resized.`,
    };
  }

  return { valid: true };
}
